/*
 * Copyright 2025 ixPilot Teknoloji A.Ş. (ixpilot2021@gmail.com)
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, modification, or distribution of this software,
 * via any medium, is strictly prohibited.
 *
 * Developed and maintained by ixPilot Teknoloji A.Ş.
 */

import React, { useState, useEffect } from "react";
import {
  Modal, ModalOverlay, ModalContent, ModalCloseButton,
  ModalBody, ModalFooter, Button, VStack, HStack, Text, 
  Box, useToast, Badge, Spinner,
  Icon, Table, Thead, Tbody, Tr, Th, Td, TableContainer
} from "@chakra-ui/react";
import { 
  FiRefreshCw, FiSmartphone
} from "react-icons/fi";
import api from "utils/api";
import { formatDate } from "utils/dateFormatter";

const SimGecmisiModal = ({ isOpen, onClose, aracData }) => {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [gecmisData, setGecmisData] = useState([]);

  useEffect(() => {
    if (isOpen && aracData) {
      loadSimGecmisi();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, aracData]);

  const loadSimGecmisi = async () => {
    if (!aracData?.id) return;
    
    setLoading(true);
    
    try {
      const response = await api.get(`/api/devices/${aracData.id}/sim-gecmisi`);
      const result = await response.json();
      
      if (response.ok) {
        setGecmisData(result || []);
      } else {
        throw new Error(result.detail || "SIM geçmişi alınamadı");
      }
    } catch (error) {
      toast({
        title: "Hata",
        description: error.message,
        status: "error",
        duration: 4000,
        isClosable: true
      });
      setGecmisData([]);
    } finally {
      setLoading(false);
    }
  };


  if (!aracData) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" isCentered motionPreset="slideInBottom" closeOnOverlayClick={false}>
      <ModalOverlay bg="blackAlpha.400" backdropFilter="blur(4px)" />
      <ModalContent borderRadius="2xl" boxShadow="xl" m={4} maxH="90vh" overflow="hidden" bg="white" p={1}>
        
        {/* Header */}
        <Box bg="white" p={4} borderBottom="1px solid" borderColor="gray.200">
          <ModalCloseButton size="sm" />
          <VStack spacing={1} align="start">
            <Text fontSize="lg" fontWeight="medium" color="gray.800">
              SIM Geçmişi
            </Text>
            <Text fontSize="xs" color="gray.400">
              {aracData.plaka} • {aracData.firma_ismi || aracData.firma} • Mevcut SIM: {aracData.sim_no || "-"}
            </Text>
          </VStack>
        </Box>

        <ModalBody px={6} py={4} maxH="500px" overflowY="auto">
          {loading ? (
            <VStack spacing={4} py={8}>
              <Spinner size="lg" color="blue.500" />
              <Text color="gray.600">SIM geçmişi yükleniyor...</Text>
            </VStack>
          ) : gecmisData.length === 0 ? (
            <VStack spacing={4} py={8}>
              <Icon as={FiSmartphone} size={48} color="gray.400" />
              <VStack spacing={2}>
                <Text fontSize="lg" color="gray.600">
                  SIM kullanım geçmişi bulunamadı
                </Text>
                <Text fontSize="sm" color="gray.500">
                  {aracData.sim_no 
                    ? "Bu araç için henüz SIM kullanım kaydı bulunmuyor."
                    : "Bu araç için SIM numarası kayıtlı değil."}
                </Text>
              </VStack>
            </VStack>
          ) : (
            <VStack spacing={4} align="stretch">
              <HStack justify="space-between">
                <Text fontSize="sm" color="gray.600">
                  {gecmisData.length} kayıt bulundu
                </Text>
                <Button
                  size="sm"
                  variant="outline"
                  leftIcon={<FiRefreshCw />}
                  onClick={loadSimGecmisi}
                  isLoading={loading}
                >
                  Yenile
                </Button>
              </HStack>

              <TableContainer>
                <Table variant="simple" size="sm">
                  <Thead bg="gray.100">
                    <Tr>
                      <Th>Tarih</Th>
                      <Th>Telefon No</Th>
                      <Th>Not</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {gecmisData.map((item, index) => (
                      <Tr key={item.id || index} _hover={{ bg: "gray.50" }}>
                        <Td>
                          <Text fontSize="sm" fontWeight="semibold">
                            {formatDate(item.timestamp)}
                          </Text>
                        </Td>
                        <Td>
                          <Badge colorScheme="blue" fontSize="sm" fontFamily="mono">
                            {item.tel_no}
                          </Badge>
                        </Td>
                        <Td>
                          <Text fontSize="sm" maxW="300px" isTruncated title={item.note}>
                            {item.note || "-"}
                          </Text>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            </VStack>
          )}
        </ModalBody>

        <ModalFooter bg="white" px={6} py={4}>
          <Button variant="ghost" onClick={onClose} size="sm">
            Kapat
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default SimGecmisiModal;

